import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import FilledButton from 'components/Buttons/FilledButton';

import { renderToast } from 'components/Informations/Toast/utils';
import ModalContainer from 'components/ModalContainer';

import SelectField from 'components/FormGroup/SelectField';
import MultipleChoice from 'components/Questions/MultipleChoice';

import useFetch from 'hooks/useFetch';
import endpoints from 'services/endpoints';

import * as S from './styles';

const { getEmployeesToSelect } = endpoints;

const JustifyOverlay = ({
  headText,
  hasEmployeeSelection,
  selectLabel,
  isOpen,
  setIsOpen,
  data,
}) => {
  const {
    employeeName,
    justifications,
    requestAction,
    setObserver,
    additionalContent,
  } = data;

  const [toastVisibility, setToastVisibility] = useState(false);
  const [toastInfos, setToastInfos] = useState({
    type: '',
    message: '',
    closeTime: '',
  });

  const [justificationOptions, setJustificationOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState('');

  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');

  const [allEmployees] = useFetch(getEmployeesToSelect);

  useEffect(() => {
    if (hasEmployeeSelection) {
      (async () => {
        const employeesData = await allEmployees;
        const employeesSelect = employeesData.map(employee => ({
          value: employee.id_funcionario,
          label: employee.nome,
        }));

        setEmployeeOptions(employeesSelect);
      })();
    }

    setJustificationOptions(justifications);
  }, [hasEmployeeSelection, allEmployees, justifications]);

  const handleChoiceChange = event => {
    const value = Number(event.target.value);
    setSelectedOption(value);
  };

  const saveAndClose = async () => {
    const objectToSend = {
      ...additionalContent,
      selectedOption,
      selectedEmployee: selectedEmployee || '',
    };

    try {
      await requestAction(objectToSend);

      setToastVisibility(true);
      setToastInfos({
        type: 'information',
        message: 'Ação concluída com sucesso',
        closeTime: 6000,
      });
      setSelectedOption('');

      setTimeout(() => {
        setIsOpen(false);
      }, 3000);

      return setObserver(current => !current);
    } catch {
      setToastInfos({
        type: 'error',
        message: 'Erro ao concluir ação. Tenta novamente',
        closeTime: 6000,
      });
      setSelectedOption('');

      return setObserver(current => !current);
    }
  };

  return (
    <>
      {toastVisibility
        && renderToast(
          toastInfos.type,
          toastInfos.message,
          toastInfos.closeTime,
          setToastVisibility,
        )}

      <ModalContainer headText={headText} isOpen={isOpen} setIsOpen={setIsOpen}>
        <S.Infos>
          <S.Label>Funcionário</S.Label>
          <S.EmployeeName>{employeeName}</S.EmployeeName>
        </S.Infos>

        {hasEmployeeSelection && (
          <SelectField
            name="selectEmployee"
            placeholderText="Selecione um funcionário..."
            hasLabel
            labelText={selectLabel}
            options={employeeOptions}
            value={employeeOptions.find(
              employee => employee.value === selectedEmployee || '',
            )}
            handleChange={({ value }) => setSelectedEmployee(value)}
          />
        )}

        <S.Options>
          <S.FormGroup>
            <MultipleChoice
              labelText="Justificativa (*)"
              hasLabelColor
              handleChange={handleChoiceChange}
              options={justificationOptions}
              selectedOption={selectedOption}
            />
          </S.FormGroup>
        </S.Options>
        <S.ActionButtons>
          <FilledButton
            buttonClass="cancel"
            text="Cancelar"
            handleClick={() => setIsOpen(false)}
          />
          <FilledButton
            disabled={!selectedOption}
            buttonClass="confirm"
            text="Salvar"
            handleClick={saveAndClose}
          />
        </S.ActionButtons>
      </ModalContainer>
    </>
  );
};

export default JustifyOverlay;

JustifyOverlay.defaultProps = {
  hasEmployeeSelection: false,
};

JustifyOverlay.propTypes = {
  headText: PropTypes.string.isRequired,
  hasEmployeeSelection: PropTypes.bool,
  selectLabel: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  data: PropTypes.objectOf(
    PropTypes.shape({
      employeeName: PropTypes.string,
      justifications: PropTypes.array,
      requestAction: PropTypes.func,
      setObserver: PropTypes.bool,
      additionalContent: PropTypes.object,
    }),
  ).isRequired,
};
